/**
 * @fileoverview Hook the Actions Layer for managing actions of the context menu of InboxChatListMC
 * @module useInboxChatListContextMenuActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 3.1.0
 */

/**
 * Receives the conversation and returns the handlers for each action
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeChatMessagingState,
  changeCustomerAgent,
  setChatReadToggleState
} from '../../../../actions/inbox/conversationActions';
import {
  buildAssignAgentPayload,
  buildMessagingStatePayload,
  buildReadStatePayload,
  getConversationActionTargets,
  isConversationInSelection
} from '../utils/bulkConversationActions';

/**
 * Here is the real logic of each action
 */
const useInboxChatListContextMenuActions = ({
  toggleSelectingConversations,
  toggleConversationSelection,
  exitSelectingConversations,
  isSelectingConversations,
  selectedConversations = []
}) => {
  const dispatch = useDispatch();
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser || {});
  const canAssignChatsToAgents = Boolean(currentRetailerUser?.can_assign_chats_to_agents);

  const getHandlers = useCallback((conversation) => {
    const targetConversations = getConversationActionTargets(conversation, selectedConversations);

    return ({
      targetConversations,
      transfer: (agentValue) => {
        if (!canAssignChatsToAgents && Number(agentValue) !== Number(currentRetailerUser?.id)) {
          return;
        }

        const { body, options } = buildAssignAgentPayload(targetConversations, agentValue);

        if (body.length > 0) {
          dispatch(changeCustomerAgent(body, options));
        }
        // Transferir la selección la vacía, igual que desde el encabezado (ver useQuickFiltersUI).
        if (isConversationInSelection(conversation, selectedConversations)) {
          exitSelectingConversations?.();
        }
      },
      markUnread: () => {
        dispatch(setChatReadToggleState(buildReadStatePayload(targetConversations), { unreadState: true }));
      },
      markState: (statusChatValue) => {
        const { body, options } = buildMessagingStatePayload(targetConversations, statusChatValue);

        if (body.length > 0) {
          dispatch(changeChatMessagingState(body, options));
        }
      },
      select: () => {
        if (!isSelectingConversations) {
          toggleSelectingConversations();
        }
        toggleConversationSelection({
          id: conversation.id,
          platform: conversation.platform,
          assignedAgent: conversation.assignedAgent,
          statusChat: Number(conversation.statusChat)
        });
      }
    });
  }, [
    canAssignChatsToAgents,
    currentRetailerUser?.id,
    selectedConversations,
    toggleConversationSelection,
    toggleSelectingConversations,
    exitSelectingConversations,
    isSelectingConversations,
    dispatch
  ]);

  return { getHandlers };
};

export default useInboxChatListContextMenuActions;
